import { useState, useEffect, useCallback, useRef } from "react";

import clsx from "clsx";

import { Listbox, Transition } from "@headlessui/react";
import { CheckIcon, SelectorIcon } from "@heroicons/react/solid";

import FieldLabel from "../Field-Label";
import useKeyPress from "../utils/key-press";

import {
  MultiSelectProps,
  getOptionValue,
  Option,
  optionIsOptionType,
} from "./types";

function MultiSelect<T extends number | string>({
  label,
  labelTags,
  name,
  selected,
  onChange,
  help,
  error,
  required,
  enableSelectAll = true,
  placeholder,
  fullListing,
  ...rest
}: MultiSelectProps<T>) {
  const node = useRef();
  const [isOpen, setIsOpen] = useState(false);
  const [currOption, setCurrOption] = useState<T>();

  // Escape
  const escapePress = useKeyPress("Escape", node);
  useEffect(() => {
    if (escapePress) {
      setIsOpen(false);
    }
  }, [escapePress, setIsOpen]);

  const onAddItem = useCallback(
    (value: T) => {
      if (!selected.includes(value)) {
        onChange([...selected, value]);
      }
      setCurrOption(value);
      setIsOpen(true);
    },
    [selected, onChange, setIsOpen]
  );
  const onRemoveItem = useCallback(
    (value: T) => {
      if (selected.includes(value)) {
        onChange(selected.filter((v) => v !== value));
      }
      setCurrOption(value);
      setIsOpen(true);
    },
    [selected, onChange, setIsOpen]
  );

  const options = "options" in rest ? rest.options : rest.allowedValues;
  const selectedOptions = (selected || [])
    .map((s) =>
      (options as (Option<T> | T)[]).find((opt) => getOptionValue(opt) === s)
    )
    .filter((opt) => opt !== null && opt !== undefined && opt !== "") as (
    | Option<T>
    | T
  )[];
  const links = (
    <div className="flex flex-row-reverse">
      <span
        className="ml-2 text-primary-main cursor-pointer"
        onClick={() => {
          onChange([]);
        }}
        role="button"
      >
        DE-SELECT ALL
      </span>
      |
      <span
        className="mr-2 text-primary-main cursor-pointer"
        onClick={() => {
          onChange((options as (Option<T> | T)[]).map(getOptionValue));
        }}
        role="button"
      >
        SELECT ALL
      </span>
    </div>
  );

  const handleClick = (e) => {
    // @ts-ignore
    if (node.current?.contains(e.target)) {
      return;
    }
    setIsOpen(false);
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClick);

    return () => {
      document.removeEventListener("mousedown", handleClick);
    };
  }, []);

  if (fullListing) {
    return (
      <div>
        {label && (
          <FieldLabel
            name={name}
            label={label}
            error={!!error}
            required={required}
            tags={labelTags}
          />
        )}
        {enableSelectAll && links}
        <div className="md:grid md:grid-cols-3 md:gap-4">
          {options.map((opt) => {
            const value = getOptionValue(opt);
            return (
              <div
                key={value}
                role="option"
                aria-selected={selected.includes(value)}
                aria-label={getOptionValue(opt)}
              >
                <p
                  className="cursor-pointer"
                  onClick={() =>
                    selected.includes(value)
                      ? onChange(selected.filter((s) => s !== value))
                      : onChange([...selected, value])
                  }
                >
                  <span className="pr-1 mr-1">
                    <input
                      key={`input-${value}`}
                      type="checkbox"
                      className="form-checkbox m-0 p-0 focus:ring-0"
                      checked={selected.includes(value)}
                    />
                  </span>
                  {optionIsOptionType(opt) ? opt.label : opt}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div ref={node}>
      <Listbox
        value={currOption}
        onChange={(v) => {
          !selected.includes(v) ? onAddItem(v) : onRemoveItem(v);
        }}
      >
        {({ open }) => (
          <>
            {label ? (
              <Listbox.Label>
                <FieldLabel
                  name={name}
                  label={label}
                  error={!!error}
                  required={required}
                  tags={labelTags}
                />
                {enableSelectAll && links}
              </Listbox.Label>
            ) : enableSelectAll ? (
              <Listbox.Label>{links}</Listbox.Label>
            ) : null}
            <div className="relative mt-1">
              {/* <Listbox.Button as={Btn} /> */}
              <Listbox.Button
                as={"button"}
                className={clsx(
                  "relative w-full",
                  "py-1 pl-3 pr-10 text-left",
                  "bg-white cursor-default",
                  "border-b-2",
                  "border-gray-300",
                  "hover:border-gray-400",
                  "focus:border-primary-main",
                  "focus:text-primary-main",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-opacity-75 focus-visible:ring-white focus-visible:ring-offset-orange-300 focus-visible:ring-offset-2 focus-visible:border-indigo-500",
                  "sm:text-sm md:text-base"
                )}
                onClick={() => setIsOpen(!isOpen)}
              >
                <span
                  className="flex flex-wrap"
                  onClick={() => setIsOpen(true)}
                >
                  {selectedOptions.length === 0 && placeholder && (
                    <span className="block truncate text-gray-500">
                      {placeholder}
                    </span>
                  )}
                  {(isOpen || open
                    ? selectedOptions
                    : selectedOptions.slice(0, 2)
                  ).map((opt, ind) => (
                    <span
                      className={
                        "flex items-center bg-gray-300 text-gray-700 pl-2 pr-1 my-1 ml-1 rounded-xl text-sm"
                      }
                      key={`selected-item-${ind}`}
                    >
                      {optionIsOptionType(opt)
                        ? !!opt.displayStr
                          ? opt.displayStr
                          : typeof opt.label === "string"
                          ? opt.label
                          : opt.value.toLocaleString()
                        : opt.toLocaleString()}{" "}
                      <span
                        className="ml-2 cursor-pointer"
                        role="button"
                        aria-label="Remove from Selection"
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          onRemoveItem(getOptionValue(opt));
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemoveItem(getOptionValue(opt));
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          className="h-5 w-5 text-gray-600"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </span>
                    </span>
                  ))}

                  {!(isOpen || open) && selected.length > 2 && (
                    <span className="flex items-center bg-gray-300 text-gray-700 px-2 my-1 mx-1 rounded-xl">
                      + {selected.length - 2} more item
                      {selected.length > 3 ? "s" : ""}
                    </span>
                  )}
                  <span className="absolute inset-y-0 right-0 flex items-center pr-2">
                    <SelectorIcon
                      className="w-5 h-5 text-gray-400 pointer-events-none"
                      aria-hidden="true"
                    />
                  </span>
                </span>
              </Listbox.Button>
              <Transition
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
                show={isOpen || open}
              >
                <Listbox.Options className="absolute w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm md:text-base z-50">
                  {options.map((opt) => {
                    const value = optionIsOptionType(opt) ? opt.value : opt;
                    const optIsSelected = selected.includes(value);
                    return (
                      <Listbox.Option key={value} value={value}>
                        {({ active }) => (
                          <div
                            className={clsx(
                              active
                                ? "text-blue-900 bg-blue-100"
                                : "text-gray-900",
                              "cursor-default select-none relative py-2 pl-10 pr-4"
                            )}
                          >
                            <span
                              className={clsx(
                                optIsSelected ? "font-medium" : "font-normal",
                                "block truncate"
                              )}
                              onClick={() =>
                                optIsSelected
                                  ? onRemoveItem(value)
                                  : onAddItem(value)
                              }
                            >
                              {optionIsOptionType(opt)
                                ? opt.displayStr || opt.label
                                : opt}
                              {optIsSelected && (
                                <span
                                  className={
                                    "text-blue-600 absolute inset-y-0 left-0 flex items-center pl-3"
                                  }
                                >
                                  <CheckIcon
                                    className="w-5 h-5"
                                    aria-hidden="true"
                                  />
                                </span>
                              )}
                            </span>
                          </div>
                        )}
                      </Listbox.Option>
                    );
                  })}
                </Listbox.Options>
              </Transition>
            </div>
          </>
        )}
      </Listbox>
      {help && (
        <div
          className={clsx(
            "text-sm",
            "pt-1",
            !!error ? "text-error-dark" : "text-gray-700"
          )}
        >
          {help}
        </div>
      )}
      {error && <div className="text-error-dark text-sm pt-1">{error}</div>}
    </div>
  );
}

export default MultiSelect;
